// Updates.tsx
import React, { useState } from 'react'
import { Button, Card, Collapse, Typography } from 'antd'
import getUpdates2026Panels from './updates/Updates2026'

const { Title } = Typography

interface UpdatesProps {
  headerCardStyle?: React.CSSProperties
  headerCardHeadStyle?: React.CSSProperties
  headerCardBodyStyle?: React.CSSProperties
  panelStyle?: React.CSSProperties
  variant?: 'card' | 'embedded'
}

type UpdatePanelProps = {
  header?: React.ReactNode
  children?: React.ReactNode
}

const getUpdateCountLabel = (count: number) => (
  `${count} update${count === 1 ? '' : 's'}`
)

const knownMonthUpdateCounts: Record<string, number> = {
  'May 2026': 1,
  'April 2026': 4,
  'March 2026': 1,
  'February 2026': 4,
  'January 2026': 4,
}

const countNestedUpdatePanels = (children: React.ReactNode): number => {
  let count = 0
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<UpdatePanelProps>(child)) {
      return
    }
    if (child.props.children) {
      count += React.Children.count(child.props.children)
    }
  })
  return count
}

const addUpdateCountHeaders = (panels: React.ReactNode[]) => (
  panels.map((panel) => {
    if (!React.isValidElement<UpdatePanelProps>(panel) || typeof panel.props.header !== 'string') {
      return panel
    }
    const updateCount = countNestedUpdatePanels(panel.props.children) || knownMonthUpdateCounts[panel.props.header] || 0
    if (updateCount <= 0) {
      return panel
    }
    return React.cloneElement(panel, {
      header: (
        <span className="updates-month-header">
          <span>{panel.props.header}</span>
          <span className="updates-month-count">{getUpdateCountLabel(updateCount)}</span>
        </span>
      ),
    })
  })
)

const Updates: React.FC<UpdatesProps> = ({
  headerCardStyle = {},
  headerCardHeadStyle = {},
  headerCardBodyStyle = {},
  panelStyle = {},
  variant = 'card'
}) => {
  const [showOlderUpdates, setShowOlderUpdates] = useState(false)
  const [olderPanels, setOlderPanels] = useState<React.ReactNode[] | null>(null)
  const [isLoadingOlderUpdates, setIsLoadingOlderUpdates] = useState(false)
  const [olderUpdatesError, setOlderUpdatesError] = useState(false)

  const handleLoadOlderUpdates = async () => {
    setShowOlderUpdates(true)
    if (olderPanels) {
      return
    }
    setIsLoadingOlderUpdates(true)
    setOlderUpdatesError(false)
    try {
      const [updates2025, updates2024] = await Promise.all([
        import('./updates/Updates2025'),
        import('./updates/Updates2024'),
      ])
      setOlderPanels([
        ...addUpdateCountHeaders(updates2025.default(panelStyle)),
        ...addUpdateCountHeaders(updates2024.default(panelStyle)),
      ])
    } catch {
      setOlderUpdatesError(true)
    } finally {
      setIsLoadingOlderUpdates(false)
    }
  }

  const updatesContent = (
    <>
      <Collapse
        className={variant === 'embedded' ? 'about-updates-collapse' : undefined}
        bordered={false}
        expandIconPosition="end"
      >
        {addUpdateCountHeaders(getUpdates2026Panels(panelStyle))}
        {showOlderUpdates && olderPanels ? olderPanels : null}
      </Collapse>
      {!showOlderUpdates ? (
        <div className={variant === 'embedded' ? 'about-updates-action' : undefined} style={variant === 'card' ? { paddingTop: 8, textAlign: 'center' } : undefined}>
          <Button type="link" onClick={handleLoadOlderUpdates}>
            Load older updates
          </Button>
        </div>
      ) : null}
      {showOlderUpdates && !olderPanels && (isLoadingOlderUpdates || olderUpdatesError) ? (
        <div className={variant === 'embedded' ? 'about-updates-state' : undefined} style={variant === 'card' ? { color: 'var(--text-secondary)', paddingTop: 8, textAlign: 'center' } : undefined}>
          {olderUpdatesError ? 'Unable to load older updates right now.' : 'Loading older updates...'}
        </div>
      ) : null}
    </>
  )

  if (variant === 'embedded') {
    return (
      <>
        <div className="about-updates-heading">
          <div>
            <span className="about-kicker">Updates</span>
            <h2 id="about-updates-title">Updates</h2>
            <p>Recent release notes and product improvements.</p>
          </div>
        </div>
        {updatesContent}
      </>
    )
  }

  return (
    <div id="updates">
      <Card
        title={<Title level={3} style={{ margin: 0, color: 'var(--text-main)' }}>Updates</Title>}
        style={headerCardStyle}
        headStyle={headerCardHeadStyle}
        bodyStyle={headerCardBodyStyle}
        bordered={false}
      >
        {updatesContent}
      </Card>
    </div>
  )
}

export default Updates
